import React from 'react';
import { NextSeo, LocalBusinessJsonLd } from 'next-seo';
import { createMerchantPath } from '@domain/merchants/hooks/useMerchantPath';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  noIndex?: boolean;
  canonical?: string;
  menuData?: Array<{ name: string; [key: string]: any }>;
  // Server-side data props for better SEO
  merchantData?: IUseMerchantData | IMerchant;
  merchantGroupData?: IUseMerchantGroup | IMerchantGroup;
  // Image URLs for server-side rendering (no JavaScript required)
  logoUrl?: string;
  backgroundUrl?: string;
  // Merchant ID for canonical URL generation (no JavaScript required)
  merchantId?: string;
}

export const SEO: React.FC<SEOProps> = React.memo(
  ({
    title,
    description,
    keywords,
    image,
    noIndex = false,
    canonical,
    menuData,
    merchantData,
    merchantGroupData,
    logoUrl,
    backgroundUrl,
    merchantId,
  }) => {
    // Use the data passed as props (from server-side rendering)
    const merchant = merchantData;
    const group = merchantGroupData;

    // Generate canonical URL using the actual URL structure
    const canonicalUrl = (() => {
      if (canonical) return canonical;

      // Use createMerchantPath to generate the proper URL structure
      // It handles cases where merchant or group data might not be available yet
      const path = createMerchantPath({
        merchantId: merchant?.merchantId || merchantId,
        merchant,
        groupId: group?.id,
      });
      return `https://order.spoton.com${path}`;
    })();

    // Process menu data for structured data
    const menuSections = (() => {
      if (!menuData) return [];

      const descBlockers = ['feature', 'recommend', 'favorite'];

      return menuData
        .filter((item) => {
          const lcName = item?.name?.toLowerCase() || '';
          return !descBlockers.some((blocker) => lcName.includes(blocker));
        })
        .map((item) => ({
          '@type': 'MenuSection',
          name: item.name ? item.name.charAt(0).toUpperCase() + item.name.slice(1) : 'Menu Item', // Capitalize first letter
        }))
        .slice(0, 10); // Limit to 10 sections to keep structured data manageable
    })();

    // Generate meta tags with stable dependencies
    const metaTitle = (() => {
      if (title) return title;
      if (!merchant?.name || !group?.name) return 'Order Online';
      return `Order from ${merchant.name} - ${group.name} | Online Ordering`;
    })();

    const metaDescription = (() => {
      if (description) return description;
      if (!merchant?.name || !group?.name) return 'Order delicious food online with SpotOn';
      return `Order delicious food online from ${merchant.name}. ${group.name} offers pickup, delivery, and dine-in options. Fast, easy online ordering.`;
    })();

    const metaKeywords = (() => {
      if (keywords) return keywords;
      if (!merchant?.name || !group?.name)
        return 'online ordering, restaurant, delivery, pickup, dine-in, food';
      return `${merchant.name}, ${group.name}, online ordering, restaurant, delivery, pickup, dine-in, food`;
    })();

    const metaImage = (() => {
      if (image) return image;
      return logoUrl || backgroundUrl;
    })();

    // Stable Open Graph config
    const openGraphConfig = (() => ({
      title: metaTitle,
      description: metaDescription,
      type: 'website',
      locale: 'en_US',
      site_name: merchant?.name || group?.name || 'Restaurant',
      url: canonicalUrl,
      images: metaImage
        ? [
            {
              url: metaImage,
              width: 1200,
              height: 630,
              alt: `${merchant?.name || group?.name || 'Restaurant'} logo`,
            },
          ]
        : [],
    }))();

    // Stable additional meta tags
    const additionalMetaTags = (() => {
      const tags: Array<{ name: string; content: string } | { property: string; content: string }> =
        [
          {
            name: 'keywords',
            content: metaKeywords,
          },
          {
            name: 'author',
            content: 'SpotOn',
          },
        ];

      // Add explicit Open Graph meta tags for Facebook debugger
      if (metaImage) {
        const ogImageTags = [
          {
            property: 'og:image',
            content: metaImage,
          },
          {
            property: 'og:image:width',
            content: '1200',
          },
          {
            property: 'og:image:height',
            content: '630',
          },
          {
            property: 'og:image:alt',
            content: `${merchant?.name || group?.name || 'Restaurant'} logo`,
          },
        ];

        tags.push(...ogImageTags);
      }

      // Add geo tags if merchant data is available
      if (merchant?.state && merchant?.city) {
        const geoTags = [
          {
            name: 'geo.region',
            content: `US-${merchant.state}`,
          },
          {
            name: 'geo.placename',
            content: merchant.city,
          },
        ];

        if (merchant.addressGeometry?.location?.lat && merchant.addressGeometry?.location?.lng) {
          geoTags.push(
            {
              name: 'geo.position',
              content: `${merchant.addressGeometry.location.lat};${merchant.addressGeometry.location.lng}`,
            },
            {
              name: 'ICBM',
              content: `${merchant.addressGeometry.location.lat}, ${merchant.addressGeometry.location.lng}`,
            }
          );
        }

        tags.push(...geoTags);
      }

      return tags;
    })();

    // Generate service types based on order types
    const serviceTypes = (() => {
      if (!merchant?.orderTypes) return [];

      const types = [];
      if (merchant.orderTypes.pickup) types.push('Takeout');
      if (merchant.orderTypes.delivery) types.push('Delivery');
      if (merchant.orderTypes.dinein) types.push('DineIn');

      return types;
    })();

    // Stable LocalBusinessJsonLd props
    const localBusinessProps = (() => {
      if (!merchant || !group) return null;

      return {
        type: 'Restaurant',
        id: merchant.merchantId,
        name: merchant.name,
        description: metaDescription,
        url: canonicalUrl,
        telephone: merchant.phone,
        address: {
          streetAddress: merchant.street1,
          addressLocality: merchant.city,
          addressRegion: merchant.state,
          postalCode: merchant.zip,
          addressCountry: 'US',
        },
        // Add geo coordinates if available
        geo:
          merchant.addressGeometry?.location?.lat && merchant.addressGeometry?.location?.lng
            ? {
                latitude: merchant.addressGeometry.location.lat.toString(),
                longitude: merchant.addressGeometry.location.lng.toString(),
              }
            : undefined,
        servesCuisine: ['Restaurant'],
        images: metaImage ? [metaImage] : undefined,
        // Custom fields for enhanced restaurant schema
        alternateName: group.name,
        logo: logoUrl || backgroundUrl,
        // acceptsReservations: true,
        serviceType: serviceTypes.length > 0 ? serviceTypes.join(', ') : undefined,
        hasMenu: {
          '@type': 'Menu',
          url: canonicalUrl,
          ...(menuSections.length > 0 && { hasMenuSection: menuSections }),
        },
        potentialAction: {
          '@type': 'OrderAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: canonicalUrl,
            inLanguage: 'en-US',
            actionPlatform: [
              'http://schema.org/DesktopWebPlatform',
              'http://schema.org/MobileWebPlatform',
            ],
          },
        },
      };
    })();

    return (
      <>
        <NextSeo
          title={metaTitle}
          description={metaDescription}
          canonical={canonicalUrl}
          openGraph={openGraphConfig}
          additionalMetaTags={additionalMetaTags}
          noindex={noIndex}
          nofollow={noIndex}
        />

        {/* Enhanced Restaurant Structured Data using LocalBusinessJsonLd */}
        {localBusinessProps && <LocalBusinessJsonLd {...localBusinessProps} />}
      </>
    );
  }
);

SEO.displayName = 'SEO';
